import { Badge, Box, Flex, Progress, Text } from '@radix-ui/themes';
import { MlbStatsHelper } from 'classes/helpers/mlb-stats.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import {
  CREATE_NEW_LIST_ID,
  PitchListHelper,
} from 'classes/helpers/pitch-list.helper';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTableHoC } from 'components/common/table';
import { ImportPitchersScalingPopover } from 'components/sections/game-data/dialogs/import-pitchers/scaling-popover';
import { GameDataContext } from 'contexts/game-data.context';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { IBaseDialog } from 'interfaces/i-dialogs';
import {
  IImportPitcherConfigurationRow,
  ImportAction,
} from 'interfaces/i-game-data';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { BuildPriority, PitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IMlbPitchExt } from 'lib_ts/interfaces/mlb-stats-api/i-pitch';
import { IMlbPlayer } from 'lib_ts/interfaces/mlb-stats-api/i-player';
import { safeFolder } from 'lib_ts/interfaces/pitches';
import { clamp, clone, first } from 'lodash';
import { useContext, useMemo, useState } from 'react';
import { MlbStatsService } from 'services/mlb-stats.service';
import { PitchListsService } from 'services/pitch-lists.service';
import { PitchesService } from 'services/pitches.service';
import { useAuthStore } from 'stores/use-auth-store';
import { useCookiesStore } from 'stores/use-cookies-store';
import useListLibraryStore from 'stores/use-list-library-store';
import { v4 } from 'uuid';

interface IProps extends IBaseDialog {
  pitchers: IMlbPlayer[];
}

const ACTION_OPTIONS: IOption[] = [
  {
    label: 'Add',
    value: ImportAction.Append,
    description: 'Add this repertoire to the pitch list.',
  },
  {
    label: 'Update',
    value: ImportAction.Merge,
    description:
      'Replace pitches previously created by this method in the pitch list.',
  },
  {
    label: 'Replace and Clear',
    value: ImportAction.Replace,
    description: 'Replace all pitches in the pitch list with this repertoire.',
  },
];

export const ImportPitchersDialog = (props: IProps) => {
  const app = useCookiesStore((s) => s.app);

  const current = useAuthStore((s) => s.current);

  const { buildPitches } = useContext(GameDataContext);
  const { machine } = useContext(MachineContext);

  const lists = useListLibraryStore((s) => s.lists);

  const listOptions = useMemo(
    // * hides the option for creating a new list since an empty selection would automatically do it
    () => PitchListHelper.getListOptions(lists, current),
    [lists, current]
  );

  const buildPriority = PitchDesignHelper.getDisplayBuildPriority(
    app.game_data_import_options.priority
  );

  const buildAndScalePitches = async (
    config: IImportPitcherConfigurationRow,
    averages: IMlbPitchExt[],
    guids: IMlbPitchExt[]
  ) => {
    // for usage values => pitch frequency
    const repertoire = ArrayHelper.firstBy(
      MlbStatsHelper.getRepertoire(guids),
      'type'
    );

    const output = await buildPitches({
      priority: app.game_data_import_options.priority,
      pitches: averages,
      isAverage: true,
    });

    // for finding these pitches later for targeted deletion when merging
    output.forEach((p) => {
      p.mlb_repertoire = {
        sportId: config.pitcher.sportId,
        season: config.pitcher.season,
        playerPk: config.pitcher.playerPk,
      };

      p.pitcher = config.pitcher.name;
      p.team = config.pitcher._mx_team_details?.name ?? null;
      p.teamPk = config.pitcher._mx_team_details?.teamPk ?? null;

      // * always overwrites frequency value w/ latest usage value
      const safeUsage = repertoire[p.type ?? PitchType.None]?.usage ?? 0;
      p.frequency = clamp(Math.round(safeUsage), 0, 100);
    });

    const isScaling = ![
      config.scaling.speed,
      config.scaling.breakZ,
      config.scaling.breakX,
    ].every((n) => n === 1);

    if (isScaling) {
      output.forEach((p) => {
        const scalingResult = PitchDesignHelper.getScaledChars(
          {
            priority: p.priority ?? BuildPriority.Default,
            bs: p.bs,
            traj: p.traj,
            breaks: p.breaks,
          },
          config.scaling
        );

        // prepend 'Scaled:' to the name if it doesn't already start with it
        p.name = p.name?.startsWith('Scaled:') ? p.name : `Scaled: ${p.name}`;

        p.plate_loc_backup = TrajHelper.getPlateLoc(scalingResult.traj);
        p.traj = scalingResult.traj;
        p.bs = scalingResult.bs;
        p.bs_hash = MachineHelper.getBsHash(scalingResult.bs, p.priority);
        p.breaks = scalingResult.breaks;
      });
    }

    // set a new _id that's unique
    output.forEach((p) => {
      p._id = v4();
    });

    return output;
  };

  const [configs, setConfigs] = useState<IImportPitcherConfigurationRow[]>(
    props.pitchers
      .sort((a, b) => StringHelper.safeSort(a.name, b.name))
      .map((p) => {
        // look for any lists have previously been
        const o = {
          _id: p._id,
          pitcher: p,
          // find the first repertoire-spawned list that matches (ignore duplicates)
          listID: lists.find((l) => {
            const { mlb_repertoire: repertoire } = l;

            if (!repertoire) {
              return false;
            }

            const { season, sportId, playerPk } = repertoire;

            return (
              season === p.season &&
              sportId === p.sportId &&
              playerPk === p.playerPk
            );
          })?._id,
          action: ImportAction.Append,
          scaling: {
            speed: 1,
            breakZ: 1,
            breakX: 1,
          },
        };

        return o;
      })
  );

  // disable inputs and buttons while processing request
  const [processing, setProcessing] = useState(false);

  // counts up to the number of configs
  const [countProcessed, setCountProcessed] = useState<number>(0);

  const columns = useMemo<ITypedTableColumn<IImportPitcherConfigurationRow>[]>(
    () => [
      {
        label: 'common.pitcher-name',
        key: '_name',
        formatFn: (c) => (
          <Flex direction="column" gap="1" p="2">
            <Text size="2">{c.pitcher.name}</Text>
            <Text size="1" color={RADIX.COLOR.NEUTRAL}>
              {c.pitcher._mx_team_details?.name ?? 'No Active Roster'}
            </Text>
          </Flex>
        ),
      },
      {
        label: 'common.pitch-list',
        key: '',
        formatFn: (c) => (
          <CommonSearchInput
            id={`select-list-${c.pitcher.playerPk}`}
            name="listID"
            options={listOptions}
            placeholder="Create Automatically"
            values={c.listID ? [c.listID] : []}
            onChange={(v) => {
              const id = first(v);

              if (id === CREATE_NEW_LIST_ID) {
                useListLibraryStore.setState({
                  dialogManage: {
                    key: Date.now(),
                    dialog: {
                      identifier: 'ImportPitchersCreateListDialog',
                      mode: 'create',
                      onCreated: (created) => {
                        setConfigs((prev) =>
                          prev.map((p) => {
                            if (p.pitcher.playerPk === c.pitcher.playerPk) {
                              p.listID = created._id;
                            }
                            return p;
                          })
                        );

                        useListLibraryStore.setState({
                          dialogManage: undefined,
                        });
                      },
                      onClose: () =>
                        useListLibraryStore.setState({
                          dialogManage: undefined,
                        }),
                    },
                  },
                });
                return;
              }

              setConfigs((prev) =>
                prev.map((p) => {
                  if (p.pitcher.playerPk !== c.pitcher.playerPk) {
                    // was not changed
                    return p;
                  }

                  // updated
                  const next = clone(p);

                  next.listID = id;

                  // if the list was unset, reset the action to append (because the others don't make sense)
                  if (!id) {
                    next.action = ImportAction.Append;
                  }

                  return next;
                })
              );
            }}
            disabled={processing}
            optional
          />
        ),
      },
      {
        label: 'common.action',
        key: '_addAction',
        thClassNameFn: () => 'width-200px',
        formatFn: (c) => {
          const options = ACTION_OPTIONS.filter((o) => {
            if (c.listID) {
              // allow any action if a list is selected
              return true;
            }

            if (o.value === ImportAction.Append) {
              // only allow appending when no list is selected
              return true;
            }

            // default disallow
            return false;
          });

          return (
            <CommonSelectInput
              id={`select-action-${c.pitcher.playerPk}`}
              name="action"
              options={options}
              value={c.action}
              onChange={(v) =>
                setConfigs((prev) =>
                  prev.map((p) => {
                    if (p.pitcher.playerPk !== c.pitcher.playerPk) {
                      // was not changed
                      return p;
                    }

                    // updated
                    const next = clone(p);
                    next.action = v as ImportAction;
                    return next;
                  })
                )
              }
              disabled={processing}
              skipSort
            />
          );
        },
      },
      {
        label: 'common.scaling',
        key: '_scaling',
        disableSort: true,
        thClassNameFn: () => 'width-100px',
        align: 'right',
        formatFn: (row) => {
          return (
            <ImportPitchersScalingPopover
              row={row}
              processing={processing}
              onApplyAll={(v) => {
                // update all rows
                setConfigs((prev) =>
                  prev.map((p) => {
                    const next = clone(p);
                    next.scaling = v;
                    return next;
                  })
                );
              }}
              onApplyOne={(pk, v) => {
                // update the specific row
                setConfigs((prev) =>
                  prev.map((p) => {
                    if (p.pitcher.playerPk !== pk) {
                      // was not changed
                      return p;
                    }

                    // updated
                    const next = clone(p);
                    next.scaling = v;
                    return next;
                  })
                );
              }}
            />
          );
        },
      },
    ],
    [processing, listOptions]
  );

  return (
    <ErrorBoundary identifier="ImportMultiplePitchersDialog">
      <CommonDialog
        identifier={props.identifier}
        title={
          <Flex gap="2" align="center">
            {t('common.import-pitchers-to-pitch-lists')}
            <Badge>{buildPriority}</Badge>
          </Flex>
        }
        width={RADIX.DIALOG.WIDTH.LG}
        vFlexHeight={RADIX.DIALOG.HEIGHT.MD}
        content={
          <Flex
            direction="column"
            gap="4"
            style={{
              height: '100%',
              width: '100%',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box>
              <p>
                We found Pitch Lists matching with your import. Choose to add,
                update, or create a new pitch list. You can also optionally
                adjust pitch scaling before importing.
              </p>

              <p>Review and confirm your changes below.</p>
            </Box>

            <CommonTableHoC
              dataTestID="ImportPitchers"
              identifier="ImportPitchersTable"
              displayColumns={columns}
              displayData={configs}
              vFlex
            />

            {processing && configs.length > 0 && (
              <Progress value={(countProcessed / configs.length) * 100} />
            )}
          </Flex>
        }
        buttons={[
          {
            label: 'common.add-to-pitch-lists',
            variant: RADIX.BUTTON.VARIANT.PRIMARY,
            disabled: processing,
            onClick: async () => {
              try {
                setProcessing(true);

                NotifyHelper.info({
                  message_md:
                    'Processing request, please wait as this may take some time...',
                });

                const warnings: {
                  msg: string;
                  config: IImportPitcherConfigurationRow;
                }[] = [];

                for (const c of configs) {
                  setCountProcessed((prev) => prev + 1);

                  // fetch pitches
                  const guids =
                    await MlbStatsService.getInstance().getPlayerPitches({
                      mode: 'pitcher',
                      player: c.pitcher,
                    });

                  if (guids.length === 0) {
                    warnings.push({
                      msg: `Failed to find pitches for ${c.pitcher.name}.`,
                      config: c,
                    });
                    continue;
                  }

                  // compute repertoire
                  const avgGuids = MlbStatsHelper.averagePitches(guids);

                  if (!avgGuids || avgGuids.length === 0) {
                    warnings.push({
                      msg: `Failed to calculate average pitches for ${c.pitcher.name}.`,
                      config: c,
                    });
                    continue;
                  }

                  // build pitches (and rebuild with scaling if necessary)
                  const pitches = await buildAndScalePitches(
                    c,
                    avgGuids,
                    guids
                  );

                  if (pitches.length === 0) {
                    warnings.push({
                      msg: `Failed to build pitches for ${c.pitcher.name}.`,
                      config: c,
                    });
                    continue;
                  }

                  const targetList =
                    // existing list
                    lists.find((l) => l._id === c.listID) ??
                    // auto-create a new list
                    (await PitchListsService.getInstance().postList({
                      _parent_def: PitchListOwner.Machine,
                      _parent_id: machine._id,

                      folder: safeFolder(c.pitcher._mx_team_details?.name),
                      name: `${c.pitcher.name} (${c.pitcher.season})`,

                      pitcher: c.pitcher.name,
                      team: c.pitcher._mx_team_details?.name,
                      hand: c.pitcher.pitchHand,

                      // we don't reliably know the position since it could
                      // depend on the game when the pitcher was inserted
                      // position: ???,

                      mlb_repertoire: {
                        sportId: c.pitcher.sportId,
                        season: c.pitcher.season,
                        playerPk: c.pitcher.playerPk,
                      },

                      // for Game Data player picker
                      teamPk: c.pitcher._mx_team_details?.teamPk,
                    }));

                  if (!targetList) {
                    warnings.push({
                      msg: `Failed to find or create target list for ${c.pitcher.name}.`,
                      config: c,
                    });
                    continue;
                  }

                  switch (c.action) {
                    case ImportAction.Append: {
                      console.debug(
                        `Appending pitches for ${c.pitcher.name} into list ${targetList.name} (${targetList._id})`
                      );

                      await PitchListsService.getInstance().addPitches({
                        listID: targetList._id,
                        data: pitches,
                      });
                      break;
                    }

                    case ImportAction.Merge: {
                      console.debug(
                        `Merging pitches for ${c.pitcher.name} into list ${targetList.name} (${targetList._id})`
                      );

                      const existingPitches =
                        await PitchesService.getInstance().getList(
                          targetList._id
                        );

                      const conflictIDs = ArrayHelper.unique(
                        existingPitches.map((ex) => {
                          const { mlb_repertoire, type } = ex;

                          if (!mlb_repertoire) {
                            // not a pitch created from this process
                            return '';
                          }

                          const { sportId, season, playerPk } = mlb_repertoire;

                          const conflict = pitches.find(
                            (p) =>
                              p.mlb_repertoire &&
                              p.mlb_repertoire.sportId === sportId &&
                              p.mlb_repertoire.season === season &&
                              p.mlb_repertoire.playerPk === playerPk &&
                              p.type === type
                          );

                          if (!conflict) {
                            // no conflicting pitch of the same type found
                            return '';
                          }

                          // mark this existing pitch for deletion
                          return ex._id;
                        })
                      );

                      if (conflictIDs.length > 0) {
                        await PitchesService.getInstance().markAsDeleted(
                          conflictIDs
                        );
                      }

                      await PitchListsService.getInstance().addPitches({
                        listID: targetList._id,
                        data: pitches,
                      });
                      break;
                    }

                    case ImportAction.Replace: {
                      console.debug(
                        `Replacing pitches for ${c.pitcher.name} into list ${targetList.name} (${targetList._id})`
                      );

                      const existingPitches =
                        await PitchesService.getInstance().getList(
                          targetList._id
                        );

                      if (existingPitches.length > 0) {
                        await PitchesService.getInstance().markAsDeleted(
                          existingPitches.map((p) => p._id)
                        );
                      }

                      await PitchListsService.getInstance().addPitches({
                        listID: targetList._id,
                        data: pitches,
                      });
                      break;
                    }

                    default: {
                      console.error(
                        `Encountered unexpected import action ${c.action}!`
                      );
                      break;
                    }
                  }
                }

                if (warnings.length > 0) {
                  console.warn(warnings);
                }

                // send toasts for the first 5 warnings
                warnings
                  .filter((_, i) => i < 5)
                  .forEach((w) =>
                    NotifyHelper.warning({
                      message_md: w.msg,
                    })
                  );

                await useListLibraryStore.getState().refreshLists(false);

                // only auto-close the dialog if there were no problems
                if (warnings.length === 0) {
                  NotifyHelper.success({
                    message_md:
                      'Pitches successfully added to their respective lists.',
                  });

                  props.onClose();
                }
              } catch (e) {
                console.error(e);

                NotifyHelper.error({
                  message_md: t('common.request-failed-msg'),
                });
              } finally {
                setProcessing(false);
              }
            },
          },
        ]}
        onClose={() => {
          if (processing) {
            NotifyHelper.warning({
              message_md:
                'Your request is still being processed, please wait...',
            });
            return;
          }

          props.onClose();
        }}
        modal
      />
    </ErrorBoundary>
  );
};
